/*
 * Copyright (c) 2026 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *  Ipivot Corporation ("Confidential Information").
 */
import { useContext, useEffect, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { Button, Form, Modal, Stack, Table } from 'react-bootstrap';
import styled, { useTheme } from 'styled-components';
import { deleteLocalSoundAsset, uploadLocalSoundAsset } from '../../data';
import { SettingsContext } from '../SettingsContext';
import { StatusContext } from '../StatusContext';
import { getDefaultSoundSettings, SOUND_EVENTS, type SoundAssetDef, type SoundEventKey, type SoundSettingsMap, useSoundManager } from '../SoundContext';

const StyledSoundSettings = styled.div<{
    $accent: string;
    $trackBg: string;
    $pageBg: string;
    $text: string;
    $textMuted: string;
}>`
    .sound-range.form-range {
        background: transparent;
    }

    .sound-range.form-range::-webkit-slider-runnable-track {
        background: ${({ $trackBg }) => $trackBg};
        border: 1px solid ${({ $accent }) => $accent};
        border-radius: 999px;
        height: 0.6rem;
    }

    .sound-range.form-range::-webkit-slider-thumb {
        -webkit-appearance: none;
        appearance: none;
        background: ${({ $accent }) => $accent};
        border: 1px solid ${({ $pageBg }) => $pageBg};
        border-radius: 50%;
        box-shadow: 0 0 0 2px ${({ $pageBg }) => $pageBg};
        height: 1.15rem;
        margin-top: -0.35rem;
        width: 1.15rem;
    }

    .sound-range.form-range::-moz-range-track {
        background: ${({ $trackBg }) => $trackBg};
        border: 1px solid ${({ $accent }) => $accent};
        border-radius: 999px;
        height: 0.6rem;
    }

    .sound-range.form-range::-moz-range-thumb {
        background: ${({ $accent }) => $accent};
        border: 1px solid ${({ $pageBg }) => $pageBg};
        border-radius: 50%;
        box-shadow: 0 0 0 2px ${({ $pageBg }) => $pageBg};
        height: 1.15rem;
        width: 1.15rem;
    }

    .sound-range.form-range:focus {
        outline: none;
    }

    .sound-range.form-range:focus::-webkit-slider-thumb {
        box-shadow: 0 0 0 0.2rem rgba(57, 255, 20, 0.2);
    }

    .sound-range.form-range:focus::-moz-range-thumb {
        box-shadow: 0 0 0 0.2rem rgba(57, 255, 20, 0.2);
    }

    .sound-switch .form-check-input {
        background-color: ${({ $trackBg }) => $trackBg};
        border-color: ${({ $accent }) => $accent};
        box-shadow: none;
    }

    .sound-switch .form-check-input:checked {
        background-color: ${({ $accent }) => $accent};
        border-color: ${({ $accent }) => $accent};
    }

    .sound-switch .form-check-input:focus {
        border-color: ${({ $accent }) => $accent};
        box-shadow: 0 0 0 0.2rem rgba(57, 255, 20, 0.2);
    }

    .sound-settings-table {
        width: 100%;
        table-layout: fixed;
    }

    .sound-settings-table th,
    .sound-settings-table td {
        overflow: hidden;
        vertical-align: top;
        padding-top: 0.4rem;
        padding-bottom: 0.4rem;
        line-height: 1.15;
    }

    .sound-settings-table th {
        padding-top: 0.35rem;
        padding-bottom: 0.35rem;
    }

    .sound-settings-table .btn.btn-sm {
        padding: 0.1rem 0.45rem;
        line-height: 1.2;
        white-space: nowrap;
    }

    .sound-settings-table .form-check {
        min-height: 1.2rem;
        margin-bottom: 0;
    }

    .sound-settings-table .form-range {
        margin-top: 0.1rem;
        margin-bottom: 0.1rem;
    }

    .sound-source-cell {
        width: 100%;
        overflow: hidden;
    }

    .sound-source-summary {
        display: block;
        width: 100%;
        max-width: 100%;
        overflow-x: auto;
        overflow-y: hidden;
        white-space: nowrap;
        padding-bottom: 0.05rem;
        line-height: 1.15;
    }

    .sound-source-summary.sound-source-custom {
        font-family: Monaco, Menlo, Consolas, 'Courier New', monospace;
        font-size: 0.85rem;
    }

    .sound-source-toolbar {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: 0.35rem;
        flex-wrap: wrap;
        line-height: 1.1;
        margin-top: 0.2rem;
    }

    .sound-source-type {
        display: inline-block;
        white-space: nowrap;
    }

    .sound-description-cell {
        line-height: 1.15;
        font-size: 0.95rem;
    }

    .sound-local-assets-bar {
        display: flex;
        gap: 0.75rem;
        align-items: stretch;
        flex-wrap: wrap;
        margin-bottom: 0.85rem;
    }

    .sound-drop-target {
        flex: 1 1 360px;
        min-height: 4.25rem;
        border: 1px dashed ${({ $accent }) => $accent};
        border-radius: 0.4rem;
        padding: 0.65rem 0.8rem;
        background: ${({ $trackBg }) => $trackBg};
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.75rem;
    }

    .sound-drop-target.sound-drop-active {
        box-shadow: 0 0 0 0.2rem rgba(57, 255, 20, 0.14);
    }

    .sound-delete-panel {
        flex: 0 1 360px;
        min-width: 280px;
        border: 1px solid ${({ $accent }) => $accent};
        border-radius: 0.4rem;
        padding: 0.65rem 0.8rem;
        background: ${({ $trackBg }) => $trackBg};
    }

    .sound-delete-row {
        display: flex;
        gap: 0.5rem;
        align-items: center;
    }

    .sound-local-select.form-select,
    .sound-local-file.form-control {
        background-color: ${({ $pageBg }) => $pageBg};
        border-color: ${({ $accent }) => $accent};
        color: ${({ $text }) => $text};
    }

    .sound-local-select.form-select:focus,
    .sound-local-file.form-control:focus {
        border-color: ${({ $accent }) => $accent};
        box-shadow: 0 0 0 0.2rem rgba(57, 255, 20, 0.2);
    }

    .sound-local-select.form-select:disabled,
    .sound-local-file.form-control:disabled {
        background-color: ${({ $trackBg }) => $trackBg};
        color: ${({ $textMuted }) => $textMuted};
        opacity: 0.8;
    }

    .sound-local-file.form-control::file-selector-button {
        background-color: ${({ $trackBg }) => $trackBg};
        border-color: ${({ $accent }) => $accent};
        color: ${({ $text }) => $text};
    }

    .sound-local-file.form-control:hover::file-selector-button {
        background-color: ${({ $accent }) => $accent};
        color: ${({ $pageBg }) => $pageBg};
    }
`;

function cloneSettings(settings: SoundSettingsMap): SoundSettingsMap {
    return Object.fromEntries(
        Object.entries(settings).map(([key, value]) => [key, { ...value }]),
    ) as SoundSettingsMap;
}

function soundSettingsEqual(left: SoundSettingsMap, right: SoundSettingsMap): boolean {
    return SOUND_EVENTS.every((entry) => {
        const a = left[entry.key];
        const b = right[entry.key];
        return a.assetId === b.assetId
            && Math.abs(a.volume - b.volume) < 0.001
            && a.enabled === b.enabled;
    });
}

function getSoundAssetById(assetId: string, soundAssets: SoundAssetDef[]) {
    return soundAssets.find((asset) => asset.id === assetId) || null;
}

function describeSoundSource(assetId: string, soundAssets: SoundAssetDef[]): string {
    const normalized = String(assetId || '').trim();
    const knownAsset = getSoundAssetById(normalized, soundAssets);
    if (knownAsset) {
        return `${knownAsset.label} (${knownAsset.fileName})`;
    }
    if (!normalized) {
        return 'Default bundled asset';
    }
    return normalized;
}

function SoundsSettings() {
    const theme = useTheme() as any;
    const accentColor = theme?.colors?.ctext || '#39ff14';
    const trackBgColor = theme?.colors?.bg3 || theme?.colors?.headerBg || '#20262d';
    const pageBgColor = theme?.colors?.bg || '#111';
    const textColor = theme?.colors?.text || '#fff';
    const textMutedColor = theme?.colors?.textMuted || '#9aa0a6';
    const sectionBorder = `1px solid ${theme?.colors?.ctext || '#ccc'}`;
    const settingsContext = useContext(SettingsContext);
    const statusContext = useContext<any>(StatusContext);
    const soundManager = useSoundManager();
    const [localSettings, setLocalSettings] = useState<SoundSettingsMap>(() => cloneSettings(soundManager.settings));
    const [localMuted, setLocalMuted] = useState<boolean>(soundManager.muted);
    const [saving, setSaving] = useState(false);
    const [sourceEditorKey, setSourceEditorKey] = useState<SoundEventKey | null>(null);
    const [sourceEditorAssetId, setSourceEditorAssetId] = useState<string>(soundManager.soundAssets[0]?.id || '');
    const [sourceEditorUrl, setSourceEditorUrl] = useState<string>('');
    const [dragActive, setDragActive] = useState(false);
    const [uploadingAsset, setUploadingAsset] = useState(false);
    const [deleteAssetId, setDeleteAssetId] = useState('');
    const [deletingAsset, setDeletingAsset] = useState(false);

    const availableAssets = soundManager.soundAssets;
    const localAssetOptions = availableAssets.filter((asset) => asset.local);

    useEffect(() => {
        setLocalSettings(cloneSettings(soundManager.settings));
        setLocalMuted(soundManager.muted);
    }, [soundManager.settings, soundManager.muted]);

    useEffect(() => {
        if (!deleteAssetId && localAssetOptions.length > 0) {
            setDeleteAssetId(localAssetOptions[0].id);
        }
        if (deleteAssetId && !localAssetOptions.some((asset) => asset.id === deleteAssetId)) {
            setDeleteAssetId(localAssetOptions[0]?.id || '');
        }
    }, [deleteAssetId, localAssetOptions]);

    const defaults = useMemo(() => getDefaultSoundSettings(), []);
    const dirty = localMuted !== soundManager.muted || !soundSettingsEqual(localSettings, soundManager.settings);

    useEffect(() => {
        settingsContext.setIsDirty(dirty);
    }, [dirty, settingsContext]);

    const changedStyle = (changed: boolean): CSSProperties => changed ? {
        borderLeft: `3px solid ${theme?.colors?.info || '#0dcaf0'}`,
        paddingLeft: '0.75rem',
    } : {};

    const handleSave = async () => {
        setSaving(true);
        try {
            await soundManager.saveSettings(localSettings, localMuted);
        } finally {
            setSaving(false);
        }
    };

    const handlePreview = async (eventKey: SoundEventKey) => {
        const value = localSettings[eventKey];
        await soundManager.warmup();
        await soundManager.playSound(eventKey, {
            ignoreMute: true,
            ignoreEnabled: true,
            assetIdOverride: value.assetId,
            volumeOverride: value.volume,
            force: true,
        });
    };

    const handleUploadFiles = async (files: FileList | File[]) => {
        const file = Array.from(files)[0];
        if (!file) {
            return;
        }
        setUploadingAsset(true);
        try {
            await uploadLocalSoundAsset(file);
            await soundManager.reloadSoundAssets();
            statusContext.postInfo(`Uploaded local sound asset ${file.name}.`);
        } catch (err: any) {
            statusContext.postError(`Failed to upload local sound asset: ${err.message}`);
        } finally {
            setUploadingAsset(false);
            setDragActive(false);
        }
    };

    const handleDeleteLocalAsset = async () => {
        const target = localAssetOptions.find((asset) => asset.id === deleteAssetId);
        if (!target) {
            return;
        }
        setDeletingAsset(true);
        try {
            await deleteLocalSoundAsset(target.fileName);
            await soundManager.reloadSoundAssets();
            statusContext.postInfo(`Deleted local sound asset ${target.fileName}.`);
        } catch (err: any) {
            statusContext.postError(`Failed to delete local sound asset: ${err.message}`);
        } finally {
            setDeletingAsset(false);
        }
    };

    const handleOpenSourceEditor = (eventKey: SoundEventKey) => {
        const source = String(localSettings[eventKey]?.assetId || '').trim();
        const bundledAsset = getSoundAssetById(source, availableAssets);
        const fallbackAssetId = SOUND_EVENTS.find((entry) => entry.key === eventKey)?.defaultAssetId || availableAssets[0]?.id || '';
        setSourceEditorKey(eventKey);
        setSourceEditorAssetId(bundledAsset?.id || fallbackAssetId);
        setSourceEditorUrl(bundledAsset ? '' : source);
    };

    const handleSelectSourceAsset = async (assetId: string) => {
        setSourceEditorAssetId(assetId);
        if (!sourceEditorKey) {
            return;
        }
        const eventSettings = localSettings[sourceEditorKey] || defaults[sourceEditorKey];
        await soundManager.warmup();
        await soundManager.playSound(sourceEditorKey, {
            ignoreMute: true,
            ignoreEnabled: true,
            assetIdOverride: assetId,
            volumeOverride: eventSettings?.volume,
            force: true,
        });
    };

    const handleCloseSourceEditor = () => {
        setSourceEditorKey(null);
        setSourceEditorUrl('');
    };

    const handleApplySourceEditor = () => {
        if (!sourceEditorKey) {
            return;
        }
        const nextSource = sourceEditorUrl.trim() || sourceEditorAssetId;
        setLocalSettings((prev) => ({
            ...prev,
            [sourceEditorKey]: {
                ...prev[sourceEditorKey],
                assetId: nextSource,
            },
        }));
        handleCloseSourceEditor();
    };

    const sourceEditorEvent = sourceEditorKey
        ? SOUND_EVENTS.find((entry) => entry.key === sourceEditorKey) || null
        : null;
    const sourceEditorDefault = sourceEditorEvent ? getSoundAssetById(sourceEditorEvent.defaultAssetId, availableAssets) : null;
    const isCustomSourceActive = sourceEditorUrl.trim().length > 0;

    return (
        <StyledSoundSettings
            $accent={accentColor}
            $trackBg={trackBgColor}
            $pageBg={pageBgColor}
            $text={textColor}
            $textMuted={textMutedColor}
        >
            <div className="d-flex justify-content-between align-items-start mb-4" style={{ borderBottom: sectionBorder, paddingBottom: '0.75rem' }}>
                <div>
                    <h3 className="mb-1">Sounds</h3>
                    <div className="text-muted">Configure event sound mappings, per-event volume, and global mute.</div>
                </div>
                {dirty && (
                    <Stack direction="horizontal" gap={2}>
                        <Button
                            variant="outline-secondary"
                            onClick={() => {
                                setLocalSettings(cloneSettings(soundManager.settings));
                                setLocalMuted(soundManager.muted);
                            }}
                            disabled={saving}
                        >
                            Clear
                        </Button>
                        <Button variant="primary" onClick={handleSave} disabled={saving}>
                            {saving ? 'Saving...' : 'Save'}
                        </Button>
                    </Stack>
                )}
            </div>

            <div className="mb-4" style={changedStyle(localMuted !== soundManager.muted)}>
                <Form.Label htmlFor="sound-muted" className="fw-semibold">Global Mute</Form.Label>
                <div className="text-muted small mb-2">Master mute affects shell event playback and the status bar speaker button.</div>
                <Form.Check
                    className="sound-switch"
                    id="sound-muted"
                    type="switch"
                    label={localMuted ? 'Muted' : 'Unmuted'}
                    checked={localMuted}
                    onChange={(e) => {
                        const nextMuted = e.target.checked;
                        setLocalMuted(nextMuted);
                        void soundManager.setMutedPersisted(nextMuted);
                    }}
                />
            </div>

            <div className="text-muted small mb-3">
                Audio engine: {soundManager.ready ? 'ready' : 'waiting for first user gesture'}.
            </div>

            <div className="sound-local-assets-bar">
                <div
                    className={`sound-drop-target${dragActive ? ' sound-drop-active' : ''}`}
                    onDragEnter={(e) => {
                        e.preventDefault();
                        setDragActive(true);
                    }}
                    onDragOver={(e) => {
                        e.preventDefault();
                        setDragActive(true);
                    }}
                    onDragLeave={(e) => {
                        e.preventDefault();
                        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                            setDragActive(false);
                        }
                    }}
                    onDrop={(e) => {
                        e.preventDefault();
                        void handleUploadFiles(e.dataTransfer.files);
                    }}
                >
                    <div>
                        <div className="fw-semibold">Upload Local Sound Asset</div>
                        <div className="text-muted small">Drag audio here to upload into data/local_assets, or pick a file.</div>
                    </div>
                    <Form.Control
                        className="sound-local-file"
                        type="file"
                        size="sm"
                        accept="audio/*,.wav,.mp3,.ogg,.m4a,.aac,.flac"
                        disabled={uploadingAsset}
                        onChange={(e) => {
                            const input = e.currentTarget as HTMLInputElement;
                            if (input.files && input.files.length > 0) {
                                void handleUploadFiles(input.files);
                                input.value = '';
                            }
                        }}
                        style={{ maxWidth: '16rem' }}
                    />
                </div>

                <div className="sound-delete-panel">
                    <div className="fw-semibold mb-1">Delete Local Asset</div>
                    <div className="text-muted small mb-2">Remove one uploaded asset from data/local_assets.</div>
                    <div className="sound-delete-row">
                        <Form.Select
                            className="sound-local-select"
                            size="sm"
                            value={deleteAssetId}
                            disabled={localAssetOptions.length === 0 || deletingAsset}
                            onChange={(e) => setDeleteAssetId(e.target.value)}
                        >
                            {localAssetOptions.length === 0 ? (
                                <option value="">No local assets</option>
                            ) : localAssetOptions.map((asset) => (
                                <option key={asset.id} value={asset.id}>{asset.fileName}</option>
                            ))}
                        </Form.Select>
                        <Button
                            size="sm"
                            variant="outline-danger"
                            disabled={!deleteAssetId || deletingAsset || localAssetOptions.length === 0}
                            onClick={() => void handleDeleteLocalAsset()}
                        >
                            Delete
                        </Button>
                    </div>
                </div>
            </div>

            <Table bordered hover responsive size="sm" className="sound-settings-table">
                <thead>
                    <tr>
                        <th style={{ width: '14%' }}>Event</th>
                        <th style={{ width: '28%' }}>Sound Source</th>
                        <th style={{ width: '16%' }}>Volume</th>
                        <th style={{ width: '10%' }}>Enabled</th>
                        <th style={{ width: '24%' }}>Description</th>
                        <th style={{ width: '8%' }}>Preview</th>
                    </tr>
                </thead>
                <tbody>
                    {SOUND_EVENTS.map((entry) => {
                        const current = localSettings[entry.key];
                        const currentAsset = getSoundAssetById(current.assetId, availableAssets);
                        const defaultValue = defaults[entry.key];
                        const changed = current.assetId !== soundManager.settings[entry.key].assetId
                            || Math.abs(current.volume - soundManager.settings[entry.key].volume) > 0.001
                            || current.enabled !== soundManager.settings[entry.key].enabled;

                        return (
                            <tr key={entry.key} style={changed ? { boxShadow: `inset 3px 0 0 ${theme?.colors?.info || '#0dcaf0'}` } : undefined}>
                                <td>
                                    <div className="fw-semibold">{entry.label}</div>
                                    <div className="text-muted small">Default: {defaultValue.assetId}</div>
                                </td>
                                <td>
                                    <div className="sound-source-cell">
                                    <div className={`sound-source-summary${currentAsset ? '' : ' sound-source-custom'}`}>
                                        {describeSoundSource(current.assetId, availableAssets)}
                                    </div>
                                    <div className="sound-source-toolbar">
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={() => handleOpenSourceEditor(entry.key)}
                                        >
                                            Choose Source
                                        </Button>
                                        <div className="text-muted small sound-source-type">
                                            {currentAsset ? (currentAsset.local ? 'Local Asset' : 'Bundled Asset') : 'Custom URL or Audio Fragment'}
                                        </div>
                                    </div>
                                    </div>
                                </td>
                                <td>
                                    <Form.Range
                                        className="sound-range"
                                        min={0}
                                        max={100}
                                        step={1}
                                        value={Math.round(current.volume * 100)}
                                        onChange={(e) => {
                                            const volume = Number(e.target.value) / 100;
                                            setLocalSettings((prev) => ({
                                                ...prev,
                                                [entry.key]: {
                                                    ...prev[entry.key],
                                                    volume,
                                                },
                                            }));
                                        }}
                                    />
                                    <div className="text-muted small" style={{ lineHeight: 1.1 }}>{Math.round(current.volume * 100)}%</div>
                                </td>
                                <td>
                                    <Form.Check
                                        className="sound-switch"
                                        type="switch"
                                        checked={current.enabled}
                                        onChange={(e) => {
                                            const enabled = e.target.checked;
                                            setLocalSettings((prev) => ({
                                                ...prev,
                                                [entry.key]: {
                                                    ...prev[entry.key],
                                                    enabled,
                                                },
                                            }));
                                        }}
                                    />
                                </td>
                                <td className="sound-description-cell">
                                    <div>{entry.description}</div>
                                </td>
                                <td>
                                    <Button variant="outline-secondary" size="sm" onClick={() => handlePreview(entry.key)}>
                                        Play
                                    </Button>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </Table>

            <Modal show={sourceEditorKey !== null} onHide={handleCloseSourceEditor} centered>
                <Modal.Header
                    closeButton
                    style={{
                        backgroundColor: trackBgColor,
                        borderBottom: `1px solid ${accentColor}`,
                        color: theme?.colors?.text || '#fff',
                    }}
                >
                    <Modal.Title>
                        Sound Source{sourceEditorEvent ? `: ${sourceEditorEvent.label}` : ''}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body
                    style={{
                        backgroundColor: pageBgColor,
                        color: theme?.colors?.text || '#fff',
                    }}
                >
                    <div className="mb-3 text-muted small">
                        Choose one of the bundled sounds or enter a custom source below. Custom values should be an `https:` URL, a `file:` URL, or a URL-encoded audio fragment. If a custom source is filled in, the bundled asset selector is disabled and ignored.
                    </div>

                    {sourceEditorEvent ? (
                        <div className="mb-3">
                            <div className="fw-semibold">Event</div>
                            <div className="text-muted small">{sourceEditorEvent.description}</div>
                            {sourceEditorDefault ? (
                                <div className="text-muted small mt-1">
                                    Default asset: {sourceEditorDefault.label} ({sourceEditorDefault.fileName})
                                </div>
                            ) : null}
                        </div>
                    ) : null}

                    <Form.Group className="mb-3">
                        <Form.Label>Bundled Asset</Form.Label>
                        <Form.Select
                            value={sourceEditorAssetId}
                            disabled={isCustomSourceActive}
                            onChange={(e) => {
                                void handleSelectSourceAsset(e.target.value);
                            }}
                            style={{
                                backgroundColor: isCustomSourceActive ? trackBgColor : pageBgColor,
                                borderColor: accentColor,
                                color: theme?.colors?.text || '#fff',
                                opacity: isCustomSourceActive ? 0.65 : 1,
                            }}
                        >
                            {availableAssets.map((asset) => (
                                <option key={asset.id} value={asset.id}>
                                    {asset.label} ({asset.fileName})
                                </option>
                            ))}
                        </Form.Select>
                        <div className="text-muted small mt-1">
                            {isCustomSourceActive
                                ? 'Disabled because a custom source is set below.'
                                : 'Use one of the bundled app sounds.'}
                        </div>
                    </Form.Group>

                    <Form.Group>
                        <Form.Label>Custom URL or URL-Encoded Audio Fragment</Form.Label>
                        <Form.Control
                            type="text"
                            value={sourceEditorUrl}
                            placeholder="https://..., file:///..., or a URL-encoded audio fragment"
                            onChange={(e) => setSourceEditorUrl(e.target.value)}
                            style={{
                                backgroundColor: pageBgColor,
                                borderColor: accentColor,
                                color: theme?.colors?.text || '#fff',
                            }}
                        />
                        <div className="text-muted small mt-1">
                            Paste a custom `https:` URL, `file:` URL, or URL-encoded audio fragment here, or leave this blank to use the bundled asset selection above.
                        </div>
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer
                    style={{
                        backgroundColor: trackBgColor,
                        borderTop: `1px solid ${accentColor}`,
                    }}
                >
                    <Button variant="outline-secondary" onClick={handleCloseSourceEditor}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleApplySourceEditor}>
                        Apply
                    </Button>
                </Modal.Footer>
            </Modal>
        </StyledSoundSettings>
    );
}

export default SoundsSettings;